import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Fab,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Snackbar,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    Translate as TranslateIcon,
    Verified as VerifiedIcon,
} from '@mui/icons-material';
import { Certifications, Education, Projects, Skills, WorkExperience } from '../types';
import { Log } from '../utils/Log';
import { SectionTypes } from '../utils/enums';
import { EditEducation } from './EditEducation';
import { Utility } from '../utils/Utility';
import { EducationAPI } from '../services/API/EducationAPI';
import { WorkExperienceAPI } from '../services/API/WorkExperienceAPI';
import { EditWorkExperience } from './EditWorkExperience';
import { SkillAPI } from '../services/API/SkillAPI';
import { EditSkill } from './EditSkill';
import { EditCertification } from './EditCertification';
import { CertificationAPI } from '../services/API/CertificationAPI';
import dayjs from 'dayjs';
import { ProjectAPI } from '../services/API/ProjectAPI';
import { EditProject } from './EditProject';
import { LanguageAPI } from '../services/API/LanguageAPI';
import { Languages } from '../types/Models/Languages';
import { EditLanguage } from './EditLanguage';
import { useTranslation } from 'react-i18next';
import {AVAILABLE_LANGUAGES, TranslationDialog} from "../components/Translationdialog";
import {TranslationAPI} from "../services/API/TranslationAPI";

// ─────────────────────────────────────────────────────────────────────────────

interface GenericListPageProps {
    sectionType: SectionTypes;
}

const GenericListPage: React.FC<GenericListPageProps> = ({ sectionType }) => {
    type DataItem = WorkExperience | Education | Skills | Certifications | Projects | Languages;

    const [item, setItem] = useState<DataItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [itemEdit, setItemEdit] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<DataItem | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // ── Traduzione ──
    /** Map itemId → lingue già tradotte */
    const [translatedLangs, setTranslatedLangs] = useState<Record<string, string[]>>({});
    /** Dialog singolo item */
    const [translateDialogOpen, setTranslateDialogOpen] = useState(false);
    const [translateTargetItem, setTranslateTargetItem] = useState<DataItem | null>(null);
    /** Snackbar feedback */
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const { t } = useTranslation();

    // ─────────────────────────────────────────────────────────────────────────
    // LOAD
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        loadData(sectionType);
    }, [sectionType]);

    const getLangLabel = (code: string): string => {
        const langDef = AVAILABLE_LANGUAGES.find(l => l.code === code);
        return langDef ? t(langDef.i18nKey) : code.toUpperCase();
    };

    const loadData = async (typeLoad: SectionTypes) => {
        setLoading(true);
        setItem([]);
        setError('');

        let data: DataItem[] = [];

        switch (typeLoad) {
            case SectionTypes.EDUCATION:
                data = await EducationAPI.Instance<EducationAPI>().getEducation();
                break;
            case SectionTypes.EXPERIENCE:
                data = await WorkExperienceAPI.Instance<WorkExperienceAPI>().getWorkExperience();
                break;
            case SectionTypes.SKILL:
                data = await SkillAPI.Instance<SkillAPI>().getSkills();
                break;
            case SectionTypes.CERTIFICATION:
                data = await CertificationAPI.Instance<CertificationAPI>().getCertifications();
                break;
            case SectionTypes.PROJECT:
                data = await ProjectAPI.Instance<ProjectAPI>().getProjects();
                break;
            case SectionTypes.LANGUAGE:
                data = await LanguageAPI.Instance<LanguageAPI>().getLanguages();
                break;
        }

        setItem(data);
        setLoading(false);

        // Carica le lingue tradotte per tutti gli item in background
        if (data.length > 0) {
            TranslationAPI.Instance<TranslationAPI>()
                .getAllTranslatedLanguages(typeLoad)
                .then((map: Record<string, string[]>) => setTranslatedLangs(map))
                .catch(() => { /* silenzioso: non blocca la UI */ });
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // HELPERS display
    // ─────────────────────────────────────────────────────────────────────────
    const formatMonthYear = (dateString: Date) =>
        new Date(dateString).toLocaleDateString('it-IT', { year: 'numeric', month: 'long' });

    const GetTitleElement = (i: any, typeLoad: SectionTypes): string => {
        switch (typeLoad) {
            case SectionTypes.EDUCATION:    return i['institution'];
            case SectionTypes.EXPERIENCE:   return i['company'] + ' - ' + i['role'];
            case SectionTypes.SKILL:        return i['skillName'];
            case SectionTypes.CERTIFICATION:return i['certName'];
            case SectionTypes.PROJECT:      return i['projectName'];
            case SectionTypes.LANGUAGE:     return i['language'];
        }
        return '';
    };

    const getSectionTitle = (typeLoad: SectionTypes): string => {
        switch (typeLoad) {
            case SectionTypes.EDUCATION:     return 'navigation.education';
            case SectionTypes.EXPERIENCE:    return 'navigation.workExperience';
            case SectionTypes.SKILL:         return 'navigation.skill';
            case SectionTypes.CERTIFICATION: return 'navigation.certification';
            case SectionTypes.PROJECT:       return 'navigation.project';
            case SectionTypes.LANGUAGE:      return 'navigation.language';
        }
        return '';
    };

    const GetDescriptionElement = (i: any, typeLoad: SectionTypes): string => {
        switch (typeLoad) {
            case SectionTypes.EDUCATION:
                return i['degree'] + ' - ' + formatMonthYear(i['startDate']);
            case SectionTypes.EXPERIENCE:
                return (
                    formatMonthYear(i['startDate']) +
                    ' - ' +
                    (i['endDate'] === null ? t('genericListPage.descriptionListCurrently') : formatMonthYear(i['endDate']))
                );
            case SectionTypes.CERTIFICATION:
                return (
                    dayjs(i['dateObtained']).format('DD/MM/YYYY') +
                    (i['expirationDate'] == null || i['expirationDate'] === ''
                        ? ''
                        : ' - ' + t('genericListPage.descriptionListExpire') + dayjs(i['expirationDate']).format('DD/MM/YYYY'))
                );
            case SectionTypes.PROJECT:
                return i['projectName'];
            case SectionTypes.LANGUAGE:
                return i['isNativeLanguage'] ? t('genericListPage.descriptionListNativeLanguage') : i['codeLevel'];
        }
        return '';
    };

    // ─────────────────────────────────────────────────────────────────────────
    // EDIT / ADD
    // ─────────────────────────────────────────────────────────────────────────
    const handleItemClick = (i: any) => {
        setItemEdit(i);
        setCreateDialogOpen(true);
    };

    const handleAddClick = () => {
        setItemEdit(null);
        setCreateDialogOpen(true);
    };

    const handleCloseForm = () => {
        Log.writeLogDebug('CloseForm');
        setCreateDialogOpen(false);
        loadData(sectionType);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // DELETE
    // ─────────────────────────────────────────────────────────────────────────
    const handleDeleteClick = (e: React.MouseEvent, itemData: DataItem) => {
        e.stopPropagation();
        setItemToDelete(itemData);
        setDeleteDialogOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;
        setDeleteLoading(true);
        try {
            await deleteItem(itemToDelete, sectionType);
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            await loadData(sectionType);
        } catch {
            setError(t('genericListPage.deleteError'));
        } finally {
            setDeleteLoading(false);
        }
    };

    const deleteItem = async (itemData: any, typeLoad: SectionTypes): Promise<void> => {
        switch (typeLoad) {
            case SectionTypes.EDUCATION:
                await EducationAPI.Instance<EducationAPI>().deleteEducation(itemData.id); break;
            case SectionTypes.EXPERIENCE:
                await WorkExperienceAPI.Instance<WorkExperienceAPI>().deleteWorkExperience(itemData.id); break;
            case SectionTypes.SKILL:
                await SkillAPI.Instance<SkillAPI>().deleteSkills(itemData.id); break;
            case SectionTypes.CERTIFICATION:
                await CertificationAPI.Instance<CertificationAPI>().deleteCertifications(itemData.id); break;
            case SectionTypes.PROJECT:
                await ProjectAPI.Instance<ProjectAPI>().deleteProjects(itemData.id); break;
            case SectionTypes.LANGUAGE:
                await LanguageAPI.Instance<LanguageAPI>().deleteLanguages(itemData.id); break;
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // TRADUZIONE — singolo item
    // ─────────────────────────────────────────────────────────────────────────
    const handleTranslateClick = (e: React.MouseEvent, itemData: DataItem) => {
        e.stopPropagation();
        setTranslateTargetItem(itemData);
        setTranslateDialogOpen(true);
    };

    const handleTranslateSingle = async (languageCode: string) => {
        if (!translateTargetItem) return;
        Log.writeLogDebug(translateTargetItem);
        const id = (translateTargetItem as any).id;
        Log.writeLogDebug(id);
        const result = await TranslationAPI.Instance<TranslationAPI>().translateItem({
            id: id,
            sectionType: sectionType,
            targetLanguage: languageCode,
        });
        // Aggiorna i chip localmente senza ricaricare tutto
        setTranslatedLangs(prev => ({
            ...prev,
            [String(id)]: Array.from(new Set([...(prev[String(id)] ?? []), languageCode])),
        }));
        setSnackbar({
            open: true,
            message: t('translation.successSingle', {
                lang: t(AVAILABLE_LANGUAGES.find(l => l.code === languageCode)?.i18nKey ?? ''),
            }),
            severity: 'success',
        });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // VERIFICA ISTITUTO — invio email
    // ─────────────────────────────────────────────────────────────────────────
    const [sendingEmail, setSendingEmail] = useState<Record<string, boolean>>({});

    const handleSendInstitutionEmail = async (e: React.MouseEvent, itemData: DataItem) => {
        e.stopPropagation();
        const id = (itemData as any).id;
        setSendingEmail(prev => ({ ...prev, [id]: true }));
        try {
            await EducationAPI.Instance<EducationAPI>().sendInstitutionEmail(id);
            setSnackbar({
                open: true,
                message: t('educationDialog.emailSent'),
                severity: 'success',
            });
        } catch {
            setSnackbar({
                open: true,
                message: t('educationDialog.emailError'),
                severity: 'error',
            });
        } finally {
            setSendingEmail(prev => ({ ...prev, [id]: false }));
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    if (loading && item.length === 0) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ p: 3 }}>

                {/* ── Intestazione sezione ── */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" fontWeight={700}>
                        {t(getSectionTitle(sectionType))}
                    </Typography>
                    <Divider sx={{ mt: 1 }} />
                </Box>

                {/* ── Lista principale ── */}
                <Paper sx={{ mb: 3 }}>
                    {error && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <List>
                            {item.map((itemData, index) => {
                                const id = (itemData as any).id;
                                const langs = translatedLangs[String(id)] ?? [];

                                return (
                                    <React.Fragment key={id}>
                                        <ListItemButton onClick={() => handleItemClick(itemData)}>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                                        <span>{GetTitleElement(itemData, sectionType)}</span>
                                                        {/* ── Chip verifica ── */}
                                                        {sectionType === SectionTypes.EDUCATION && (itemData as any).isVerified && (
                                                            <Tooltip title={t('educationDialog.verified')}>
                                                                <VerifiedIcon color="primary" sx={{ fontSize: '1rem', ml: 0.5 }} />
                                                            </Tooltip>
                                                        )}
                                                        {/* ── Chip lingue tradotte ── */}
                                                        {langs.length > 0 && langs.filter(code => !!code).map(code => {
                                                            const langDef = AVAILABLE_LANGUAGES.find(l => l.code === code);
                                                            return (
                                                                <Tooltip
                                                                    key={code}
                                                                    title={`${t('translation.translatedIn')} ${t(langDef?.i18nKey ?? code)}`}
                                                                >
                                                                    <Chip
                                                                        label={code.toUpperCase()}
                                                                        size="small"
                                                                        color="primary"
                                                                        variant="outlined"
                                                                        icon={<TranslateIcon sx={{ fontSize: '0.75rem !important' }} />}
                                                                        sx={{
                                                                            fontSize: '0.65rem',
                                                                            height: 20,
                                                                            '& .MuiChip-label': { px: 0.75 },
                                                                            cursor: 'default',
                                                                        }}
                                                                        onClick={e => e.stopPropagation()}
                                                                    />
                                                                </Tooltip>
                                                            );
                                                        })}
                                                    </Box>
                                                }
                                                secondary={GetDescriptionElement(itemData, sectionType)}
                                            />

                                            {/* ── Bottone traduci singolo ── */}
                                            <Tooltip title={t('translation.translateButtonTooltip')}>
                                                <IconButton
                                                    edge="end"
                                                    color="primary"
                                                    onClick={(e) => handleTranslateClick(e, itemData)}
                                                >
                                                    <TranslateIcon />
                                                </IconButton>
                                            </Tooltip>

                                            {/* ── Bottone verifica via email (solo Education) ── */}
                                            {sectionType === SectionTypes.EDUCATION && !(itemData as any).isVerified && (
                                                <Tooltip title={t('educationDialog.sendVerificationEmail')}>
                                                    <IconButton
                                                        edge="end"
                                                        color="info"
                                                        onClick={(e) => handleSendInstitutionEmail(e, itemData)}
                                                        disabled={sendingEmail[String((itemData as any).id)]}
                                                    >
                                                        {sendingEmail[String((itemData as any).id)]
                                                            ? <CircularProgress size={20} />
                                                            : <EmailIcon />
                                                        }
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            <IconButton edge="end">
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                color="error"
                                                onClick={(e) => handleDeleteClick(e, itemData)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemButton>
                                        {index < item.length - 1 && <Divider />}
                                    </React.Fragment>
                                );
                            })}

                            {item.length === 0 && !loading && (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography color="text.secondary">
                                        {t('genericListPage.emptyList')}
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    )}
                </Paper>

                {/* ── FAB Aggiungi ── */}
                <Fab
                    color="primary"
                    aria-label="add"
                    sx={{ position: 'fixed', bottom: 16, right: 16 }}
                    onClick={handleAddClick}
                >
                    <AddIcon />
                </Fab>
            </Box>

            {/* ── Dialog eliminazione ── */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {t('genericListPage.confirmationDelete')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('genericListPage.confirmationDeleteStart')}{' '}
                        <strong>{itemToDelete ? GetTitleElement(itemToDelete, sectionType) : ''}</strong>?{' '}
                        {t('genericListPage.confirmationDeleteEnd')}.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={handleDeleteCancel} disabled={deleteLoading} variant="outlined" color="inherit">
                        {t('standardUse.undoBtn')}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        disabled={deleteLoading}
                        variant="contained"
                        color="error"
                        startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                    >
                        {deleteLoading
                            ? t('genericListPage.confirmationDeleteProgress')
                            : t('genericListPage.confirmationDeleteButton')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Dialog traduzione singolo item ── */}
            <TranslationDialog
                open={translateDialogOpen}
                onClose={() => { setTranslateDialogOpen(false); setTranslateTargetItem(null); }}
                itemTitle={translateTargetItem ? GetTitleElement(translateTargetItem, sectionType) : null}
                alreadyTranslatedCodes={
                    translateTargetItem
                        ? (translatedLangs[String((translateTargetItem as any).id)] ?? [])
                        : []
                }
                onConfirm={handleTranslateSingle}
                onDelete={async (languageCode) => {
                    await TranslationAPI.Instance<TranslationAPI>()
                        .deleteTranslateItem(String((translateTargetItem as any).id), sectionType, languageCode);
                    // Aggiorna i chip localmente
                    setTranslatedLangs(prev => ({
                        ...prev,
                        [String((translateTargetItem as any).id)]: (prev[String((translateTargetItem as any).id)] ?? []).filter(l => l !== languageCode),
                    }));
                }}
            />

            {/* ── Edit dialogs ── */}
            {sectionType === SectionTypes.EDUCATION && (
                <EditEducation
                    item={Utility.ConvertToType<Education>(itemEdit)}
                    onSave={handleCloseForm} onCancel={handleCloseForm}
                    open={createDialogOpen} onClose={handleCloseForm}
                />
            )}
            {sectionType === SectionTypes.EXPERIENCE && (
                <EditWorkExperience
                    item={Utility.ConvertToType<WorkExperience>(itemEdit)}
                    onSave={handleCloseForm} onCancel={handleCloseForm}
                    open={createDialogOpen} onClose={handleCloseForm}
                />
            )}
            {sectionType === SectionTypes.SKILL && (
                <EditSkill
                    item={Utility.ConvertToType<Skills>(itemEdit)}
                    onSave={handleCloseForm} onCancel={handleCloseForm}
                    open={createDialogOpen} onClose={handleCloseForm}
                />
            )}
            {sectionType === SectionTypes.CERTIFICATION && (
                <EditCertification
                    item={Utility.ConvertToType<Certifications>(itemEdit)}
                    onSave={handleCloseForm} onCancel={handleCloseForm}
                    open={createDialogOpen} onClose={handleCloseForm}
                />
            )}
            {sectionType === SectionTypes.PROJECT && (
                <EditProject
                    item={Utility.ConvertToType<Projects>(itemEdit)}
                    onSave={handleCloseForm} onCancel={handleCloseForm}
                    open={createDialogOpen} onClose={handleCloseForm}
                />
            )}
            {sectionType === SectionTypes.LANGUAGE && (
                <EditLanguage
                    item={Utility.ConvertToType<Languages>(itemEdit)}
                    onSave={handleCloseForm} onCancel={handleCloseForm}
                    open={createDialogOpen} onClose={handleCloseForm}
                />
            )}

            {/* ── Snackbar feedback traduzione ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GenericListPage;